import React, { useState, useEffect, useCallback } from 'react';
import { csrfFetch } from '../../utils/csrfFetch';

/**
 * PromptLibraryModal - Library of saved prompts for quick reuse (SAT-79)
 *
 * Features:
 * - List view of saved prompts
 * - Create/edit/delete prompts
 * - Filter by category
 * - Search functionality
 * - Insert prompt into chat input
 */

const API_BASE = '/api';

function PromptLibraryModal({ isOpen, onClose, onInsertPrompt, contained = false }) {
  const [prompts, setPrompts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [examples, setExamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Edit/Create state
  const [isEditing, setIsEditing] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    prompt_template: '',
    category: 'General',
    tags: [],
    is_public: false
  });
  const [tagInput, setTagInput] = useState('');

  // View state
  const [activeTab, setActiveTab] = useState('my-prompts'); // 'my-prompts', 'examples'

  // Fetch prompts
  const fetchPrompts = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      if (searchQuery.trim()) params.append('search', searchQuery.trim());

      const response = await csrfFetch(`${API_BASE}/prompts/library?${params}`);
      if (!response.ok) throw new Error('Failed to fetch prompts');

      const data = await response.json();
      setPrompts(data.prompts || []);
      setCategories(data.categories || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, searchQuery]);

  // Fetch examples
  const fetchExamples = useCallback(async () => {
    try {
      const response = await csrfFetch(`${API_BASE}/prompts/examples`);
      if (!response.ok) throw new Error('Failed to fetch examples');

      const data = await response.json();
      setExamples(data.examples || []);
    } catch (err) {
      console.error('Failed to fetch examples:', err);
    }
  }, []);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        if (isEditing) {
          setIsEditing(false);
          setEditingPrompt(null);
          resetForm();
        } else {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
      fetchPrompts();
      fetchExamples();
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose, isEditing, fetchPrompts, fetchExamples]);

  // Refetch when filters change
  useEffect(() => {
    if (isOpen && activeTab === 'my-prompts') {
      fetchPrompts();
    }
  }, [selectedCategory, searchQuery, isOpen, activeTab, fetchPrompts]);

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      prompt_template: '',
      category: 'General',
      tags: [],
      is_public: false
    });
    setTagInput('');
  };

  const handleCreateNew = () => {
    setEditingPrompt(null);
    resetForm();
    setIsEditing(true);
  };

  const handleEdit = (prompt) => {
    setEditingPrompt(prompt);
    setFormData({
      title: prompt.title,
      description: prompt.description || '',
      prompt_template: prompt.prompt_template,
      category: prompt.category || 'General',
      tags: prompt.tags || [],
      is_public: prompt.is_public
    });
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!formData.title.trim() || !formData.prompt_template.trim()) {
      return;
    }

    try {
      const url = editingPrompt
        ? `${API_BASE}/prompts/${editingPrompt.id}`
        : `${API_BASE}/prompts/library`;
      const method = editingPrompt ? 'PUT' : 'POST';

      const response = await csrfFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Failed to save prompt');

      setIsEditing(false);
      setEditingPrompt(null);
      resetForm();
      fetchPrompts();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this prompt?')) return;

    try {
      const response = await csrfFetch(`${API_BASE}/prompts/${id}`, {
        method: 'DELETE'
      });

      if (!response.ok) throw new Error('Failed to delete prompt');
      fetchPrompts();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUsePrompt = async (prompt) => {
    // Record usage
    try {
      await csrfFetch(`${API_BASE}/prompts/${prompt.id}/use`, { method: 'POST' });
    } catch (err) {
      console.error('Failed to record usage:', err);
    }

    // Insert into chat
    if (onInsertPrompt) {
      onInsertPrompt(prompt.prompt_template);
    }
    onClose();
  };

  const handleUseExample = (example) => {
    if (onInsertPrompt) {
      onInsertPrompt(example.prompt_template);
    }
    onClose();
  };

  const handleSaveExample = (example) => {
    setFormData({
      title: example.title,
      description: example.description || '',
      prompt_template: example.prompt_template,
      category: example.category || 'General',
      tags: example.tags || [],
      is_public: false
    });
    setEditingPrompt(null);
    setIsEditing(true);
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
  };

  if (!isOpen) return null;

  return (
    <div
      className={`${contained ? 'absolute' : 'fixed'} inset-0 z-50 flex items-center justify-center bg-black/50`}
      onClick={(e) => e.target === e.currentTarget && !isEditing && onClose()}
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-decisiv-blue/10 rounded-lg">
              <svg className="w-5 h-5 text-decisiv-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {isEditing ? (editingPrompt ? 'Edit Prompt' : 'Create Prompt') : 'Prompt Library'}
            </h2>
          </div>
          <button
            onClick={() => {
              if (isEditing) {
                setIsEditing(false);
                setEditingPrompt(null);
                resetForm();
              } else {
                onClose();
              }
            }}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300
              hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        {isEditing ? (
          // Edit/Create Form
          <div className="p-6 overflow-y-auto flex-1">
            <div className="space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Code Review"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600
                    rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                    focus:ring-2 focus:ring-decisiv-blue focus:border-transparent"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of what this prompt does"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600
                    rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                    focus:ring-2 focus:ring-decisiv-blue focus:border-transparent"
                />
              </div>

              {/* Prompt Template */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Prompt Template *
                </label>
                <textarea
                  value={formData.prompt_template}
                  onChange={(e) => setFormData({ ...formData, prompt_template: e.target.value })}
                  placeholder="Enter your prompt template. Use {placeholder} for variables."
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600
                    rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                    focus:ring-2 focus:ring-decisiv-blue focus:border-transparent font-mono text-sm"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Tip: Use curly braces for placeholders, e.g., {'{topic}'} or {'{code}'}
                </p>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Category
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600
                    rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                    focus:ring-2 focus:ring-decisiv-blue focus:border-transparent"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tags
                </label>
                <div className="flex gap-2 mb-2 flex-wrap">
                  {formData.tags.map(tag => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 px-2 py-1 text-sm
                        bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full"
                    >
                      {tag}
                      <button
                        onClick={() => removeTag(tag)}
                        className="hover:text-red-500 transition-colors"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    placeholder="Add a tag"
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600
                      rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                      focus:ring-2 focus:ring-decisiv-blue focus:border-transparent text-sm"
                  />
                  <button
                    onClick={addTag}
                    className="px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300
                      rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // List View
          <>
            {/* Tabs and Search */}
            <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 space-y-3">
              {/* Tabs */}
              <div className="flex gap-4">
                <button
                  onClick={() => setActiveTab('my-prompts')}
                  className={`pb-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'my-prompts'
                      ? 'border-decisiv-blue text-decisiv-blue'
                      : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  My Prompts
                </button>
                <button
                  onClick={() => setActiveTab('examples')}
                  className={`pb-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'examples'
                      ? 'border-decisiv-blue text-decisiv-blue'
                      : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  Examples
                </button>
              </div>

              {activeTab === 'my-prompts' && (
                <div className="flex gap-3">
                  {/* Search */}
                  <div className="flex-1 relative">
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search prompts..."
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600
                        rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                        focus:ring-2 focus:ring-decisiv-blue focus:border-transparent"
                    />
                  </div>

                  {/* Category Filter */}
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600
                      rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                      focus:ring-2 focus:ring-decisiv-blue focus:border-transparent"
                  >
                    <option value="all">All Categories</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>

                  {/* Create Button */}
                  <button
                    onClick={handleCreateNew}
                    className="px-4 py-2 text-sm font-medium bg-decisiv-blue text-white
                      rounded-lg hover:bg-opacity-90 transition-colors flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    New
                  </button>
                </div>
              )}
            </div>

            {/* Prompt List */}
            <div className="flex-1 overflow-y-auto p-6">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-decisiv-blue border-t-transparent"></div>
                </div>
              ) : error ? (
                <div className="text-center py-12">
                  <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-full inline-block mb-3">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                    Nothing in your prompt library yet
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                    Save your favourite prompts here for quick reuse. Tap + to add your first one.
                  </p>
                  <button
                    onClick={handleCreateNew}
                    className="px-4 py-2 text-sm font-medium bg-decisiv-blue text-white
                      rounded-lg hover:bg-opacity-90 transition-colors"
                  >
                    Add Prompt
                  </button>
                </div>
              ) : activeTab === 'my-prompts' ? (
                prompts.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-full inline-block mb-3">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                      No prompts yet
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                      Create your first prompt or check out the examples tab for inspiration.
                    </p>
                    <button
                      onClick={handleCreateNew}
                      className="px-4 py-2 text-sm font-medium bg-decisiv-blue text-white
                        rounded-lg hover:bg-opacity-90 transition-colors"
                    >
                      Create Prompt
                    </button>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {prompts.map(prompt => (
                      <div
                        key={prompt.id}
                        className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg
                          hover:border-decisiv-blue/50 transition-colors group"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-medium text-gray-900 dark:text-white truncate">
                                {prompt.title}
                              </h3>
                              <span className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700
                                text-gray-600 dark:text-gray-400 rounded-full">
                                {prompt.category}
                              </span>
                            </div>
                            {prompt.description && (
                              <p className="text-sm text-gray-500 dark:text-gray-400 mb-2 line-clamp-1">
                                {prompt.description}
                              </p>
                            )}
                            <p className="text-sm text-gray-600 dark:text-gray-300 font-mono
                              bg-gray-50 dark:bg-gray-750 p-2 rounded line-clamp-2">
                              {prompt.prompt_template}
                            </p>
                            {prompt.tags && prompt.tags.length > 0 && (
                              <div className="flex gap-1 mt-2 flex-wrap">
                                {prompt.tags.map(tag => (
                                  <span
                                    key={tag}
                                    className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700
                                      text-gray-600 dark:text-gray-400 rounded-full"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                            <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                              <span>Used {prompt.usage_count || 0} times</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => handleUsePrompt(prompt)}
                              className="px-3 py-1.5 text-sm font-medium bg-decisiv-blue text-white
                                rounded-lg hover:bg-opacity-90 transition-colors"
                            >
                              Use
                            </button>
                            <button
                              onClick={() => handleEdit(prompt)}
                              className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300
                                hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                              title="Edit"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDelete(prompt.id)}
                              className="p-1.5 text-gray-400 hover:text-red-500
                                hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                              title="Delete"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              ) : (
                // Examples Tab
                <div className="grid gap-4">
                  {examples.map((example, idx) => (
                    <div
                      key={idx}
                      className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg
                        hover:border-decisiv-blue/50 transition-colors group"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-medium text-gray-900 dark:text-white">
                              {example.title}
                            </h3>
                            <span className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700
                              text-gray-600 dark:text-gray-400 rounded-full">
                              {example.category}
                            </span>
                          </div>
                          {example.description && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                              {example.description}
                            </p>
                          )}
                          <p className="text-sm text-gray-600 dark:text-gray-300 font-mono
                            bg-gray-50 dark:bg-gray-750 p-2 rounded line-clamp-3">
                            {example.prompt_template}
                          </p>
                          {example.tags && example.tags.length > 0 && (
                            <div className="flex gap-1 mt-2 flex-wrap">
                              {example.tags.map(tag => (
                                <span
                                  key={tag}
                                  className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700
                                    text-gray-600 dark:text-gray-400 rounded-full"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => handleUseExample(example)}
                            className="px-3 py-1.5 text-sm font-medium bg-decisiv-blue text-white
                              rounded-lg hover:bg-opacity-90 transition-colors"
                          >
                            Use
                          </button>
                          <button
                            onClick={() => handleSaveExample(example)}
                            className="px-3 py-1.5 text-sm font-medium border border-gray-300 dark:border-gray-600
                              text-gray-700 dark:text-gray-300 rounded-lg
                              hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-850">
          {isEditing ? (
            <>
              <button
                onClick={() => {
                  setIsEditing(false);
                  setEditingPrompt(null);
                  resetForm();
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300
                  hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!formData.title.trim() || !formData.prompt_template.trim()}
                className="px-4 py-2 text-sm font-medium bg-decisiv-blue text-white
                  rounded-lg hover:bg-opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editingPrompt ? 'Save Changes' : 'Create Prompt'}
              </button>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {activeTab === 'my-prompts'
                  ? `${prompts.length} prompt${prompts.length !== 1 ? 's' : ''} saved`
                  : `${examples.length} example${examples.length !== 1 ? 's' : ''} available`}
              </p>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium bg-decisiv-blue text-white
                  rounded-lg hover:bg-opacity-90 transition-colors"
              >
                Close
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default PromptLibraryModal;
